/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { DirectoryConfig, DirectoryState, UseDirectoryResult } from "./Directory.types";
import {
	applyFilters,
	applySort,
	assertUniqueParamKeys,
	composeParamName,
	encodeMultiValueParam,
	parseMultiValueParam,
	sliceForPage,
} from "./Directory.utils";

/**
 * Safely read URL search params. `useSearchParams()` throws outside a Next.js router
 * context (e.g. in isolated Storybook); this hook returns an empty `URLSearchParams`
 * in that case so reads never crash.
 */
export function useDirectoryRouter(): URLSearchParams {
	try {
		const params = useSearchParams();
		return params ? new URLSearchParams(params.toString()) : new URLSearchParams();
	} catch {
		return new URLSearchParams();
	}
}

/**
 * Safely read pathname — mirrors useDirectoryRouter's fallback pattern.
 */
function useSafePathname(): string {
	try {
		return usePathname() ?? "";
	} catch {
		return "";
	}
}

export function computeInitialState<T>(config: DirectoryConfig<T>, params: URLSearchParams): DirectoryState {
	const { paramPrefix, filters, sort, pagination } = config;
	const searchQueries: Record<string, string> = {};
	const singleValueFilters: Record<string, string> = {};
	const multiValueFilters: Record<string, string[]> = {};

	for (const f of filters) {
		switch (f.kind) {
			case "search": {
				const persist = f.persistInUrl ?? false;
				if (persist) {
					searchQueries[f.paramKey] = params.get(composeParamName(paramPrefix, f.paramKey)) ?? "";
				} else {
					searchQueries[f.paramKey] = "";
				}
				break;
			}
			case "tabs":
			case "tabsMulti":
			case "select": {
				const persist = f.persistInUrl ?? true;
				if (!persist) {
					singleValueFilters[f.paramKey] = "";
					break;
				}
				const raw = params.get(composeParamName(paramPrefix, f.paramKey)) ?? "";
				singleValueFilters[f.paramKey] = f.options.some((o) => o.value === raw) ? raw : "";
				break;
			}
			case "chips":
			case "combobox": {
				const persist = f.persistInUrl ?? true;
				if (!persist) {
					multiValueFilters[f.paramKey] = [];
					break;
				}
				const raw = parseMultiValueParam(params, composeParamName(paramPrefix, f.paramKey));
				const validSet = new Set(f.options.map((o) => o.value));
				multiValueFilters[f.paramKey] = raw.filter((v) => validSet.has(v));
				break;
			}
		}
	}

	const sortValue = sort ? params.get(composeParamName(paramPrefix, sort.paramKey)) || sort.default : "";

	const pageParamKey = pagination?.paramKey ?? "page";
	const rawPage = pagination ? parseInt(params.get(composeParamName(paramPrefix, pageParamKey)) ?? "1", 10) : 1;
	const page = Number.isFinite(rawPage) && rawPage >= 1 ? rawPage : 1;

	return {
		searchQueries,
		singleValueFilters,
		multiValueFilters,
		sort: sortValue,
		page,
	};
}

/**
 * Serialize the current state into URL search params (applied on top of the current URL).
 */
export function serializeState<T>(
	config: DirectoryConfig<T>,
	state: DirectoryState,
	currentParams: URLSearchParams,
): URLSearchParams {
	const params = new URLSearchParams(currentParams);
	const { paramPrefix, filters, sort, pagination } = config;

	for (const f of filters) {
		const name = composeParamName(paramPrefix, f.paramKey);
		switch (f.kind) {
			case "search": {
				const persist = f.persistInUrl ?? false;
				if (!persist) {
					params.delete(name);
					continue;
				}
				const q = state.searchQueries[f.paramKey] ?? "";
				// URLSearchParams.toString() already percent-encodes values once on
				// serialize — don't pre-encode here or "a b" becomes "a%2520b".
				if (q) params.set(name, q);
				else params.delete(name);
				break;
			}
			case "tabs":
			case "tabsMulti":
			case "select": {
				const persist = f.persistInUrl ?? true;
				if (!persist) {
					params.delete(name);
					continue;
				}
				const v = state.singleValueFilters[f.paramKey] ?? "";
				if (v) params.set(name, v);
				else params.delete(name);
				break;
			}
			case "chips":
			case "combobox": {
				const persist = f.persistInUrl ?? true;
				if (!persist) {
					params.delete(name);
					continue;
				}
				const vs = state.multiValueFilters[f.paramKey] ?? [];
				const encoded = encodeMultiValueParam(vs);
				if (encoded) params.set(name, encoded);
				else params.delete(name);
				break;
			}
		}
	}

	if (sort) {
		const sortName = composeParamName(paramPrefix, sort.paramKey);
		if (state.sort && state.sort !== sort.default) params.set(sortName, state.sort);
		else params.delete(sortName);
	}

	if (pagination) {
		const pageName = composeParamName(paramPrefix, pagination.paramKey ?? "page");
		if (state.page > 1) params.set(pageName, String(state.page));
		else params.delete(pageName);
	}

	return params;
}

/**
 * The Directory hook — manages filter / sort / pagination state, syncs to the URL,
 * and returns filtered + paginated items. Exported for headless consumption; the
 * compound component (Directory.section.tsx) wraps this and provides it via context.
 */
export function useDirectory<T>(config: DirectoryConfig<T>): UseDirectoryResult<T> {
	if (process.env.NODE_ENV !== "production") {
		assertUniqueParamKeys(
			config.filters,
			config.sort?.paramKey,
			config.pagination?.paramKey ?? (config.pagination ? "page" : undefined),
		);
	}

	const pathname = useSafePathname();
	const currentParams = useDirectoryRouter();

	// Initialize once from URL; subsequent URL changes come from our own writes.
	const [state, setState] = useState<DirectoryState>(() => computeInitialState(config, currentParams));

	// Derived data
	const filteredItems = useMemo(() => {
		const filtered = applyFilters(
			config.data,
			config.filters,
			state.searchQueries,
			state.singleValueFilters,
			state.multiValueFilters,
		);
		const sortOption = config.sort?.options.find((o) => o.value === state.sort);
		return sortOption ? applySort(filtered, sortOption) : filtered;
	}, [
		config.data,
		config.filters,
		config.sort,
		state.searchQueries,
		state.singleValueFilters,
		state.multiValueFilters,
		state.sort,
	]);

	const perPage = config.pagination?.perPage ?? 0;
	const totalPages = perPage > 0 && filteredItems.length > 0 ? Math.ceil(filteredItems.length / perPage) : 1;

	// Clamp page if filtering reduced the item set below the current page.
	useEffect(() => {
		if (state.page > totalPages) {
			setState((s) => ({ ...s, page: 1 }));
		}
	}, [state.page, totalPages]);

	const visibleItems = useMemo(
		() => (perPage > 0 ? sliceForPage(filteredItems, state.page, perPage) : filteredItems),
		[filteredItems, state.page, perPage],
	);

	useEffect(() => {
		if (config.skipRouter) return;
		try {
			const nextParams = serializeState(config, state, currentParams);
			const qs = nextParams.toString();
			const nextUrl = qs ? `${pathname}?${qs}` : pathname;
			const mode = config.routerMode ?? "replaceState";
			if (mode === "pushState") window.history.pushState({}, "", nextUrl);
			else window.history.replaceState({}, "", nextUrl);
		} catch (err) {
			config.onError?.(err instanceof Error ? err : new Error(String(err)));
		}
		// currentParams intentionally excluded from deps — we already hold the latest via serialize
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state, pathname, config.skipRouter, config.routerMode]);

	// Setters
	const setSearchQuery = useCallback((paramKey: string, query: string) => {
		setState((s) => ({ ...s, searchQueries: { ...s.searchQueries, [paramKey]: query }, page: 1 }));
	}, []);
	const setSingleValueFilter = useCallback((paramKey: string, value: string) => {
		setState((s) => ({ ...s, singleValueFilters: { ...s.singleValueFilters, [paramKey]: value }, page: 1 }));
	}, []);
	const toggleMultiValueFilter = useCallback((paramKey: string, value: string) => {
		setState((s) => {
			const prev = s.multiValueFilters[paramKey] ?? [];
			const next = prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value];
			return { ...s, multiValueFilters: { ...s.multiValueFilters, [paramKey]: next }, page: 1 };
		});
	}, []);
	const clearFilter = useCallback(
		(paramKey: string) => {
			setState((s) => {
				const filter = config.filters.find((f) => f.paramKey === paramKey);
				if (!filter) return s;
				if (filter.kind === "search") {
					return { ...s, searchQueries: { ...s.searchQueries, [paramKey]: "" }, page: 1 };
				}
				if (filter.kind === "chips" || filter.kind === "combobox") {
					return { ...s, multiValueFilters: { ...s.multiValueFilters, [paramKey]: [] }, page: 1 };
				}
				return { ...s, singleValueFilters: { ...s.singleValueFilters, [paramKey]: "" }, page: 1 };
			});
		},
		[config.filters],
	);
	const clearAllFilters = useCallback(() => {
		setState((s) => {
			const searchQueries: Record<string, string> = {};
			const singleValueFilters: Record<string, string> = {};
			const multiValueFilters: Record<string, string[]> = {};
			for (const f of config.filters) {
				if (f.kind === "search") searchQueries[f.paramKey] = "";
				else if (f.kind === "chips" || f.kind === "combobox") multiValueFilters[f.paramKey] = [];
				else singleValueFilters[f.paramKey] = "";
			}
			return { ...s, searchQueries, singleValueFilters, multiValueFilters, page: 1 };
		});
	}, [config.filters]);
	const setSort = useCallback((value: string) => {
		setState((s) => ({ ...s, sort: value, page: 1 }));
	}, []);
	const setPage = useCallback((page: number) => {
		setState((s) => ({ ...s, page }));
	}, []);

	return {
		state,
		filteredItems,
		visibleItems,
		totalPages,
		currentPage: state.page,
		totalFilteredCount: filteredItems.length,
		setSearchQuery,
		setSingleValueFilter,
		toggleMultiValueFilter,
		clearFilter,
		clearAllFilters,
		setSort,
		setPage,
	};
}
